import { useQuery } from "@tanstack/react-query";
import type { QueryKey, UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  GamePassAdminTheme,
  GamePassAdminThemeResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";

export const GAME_PASS_THEME_QUERY_KEY = ["game-pass", "theme"] as const;

/**
 * Fetches the current Game Pass theme from the PAM admin surface.
 *
 * The theme is empty everywhere right now, so an unset theme is the norm rather
 * than an error: we unwrap the `{ success, data }` envelope and fall back to an
 * empty document (`{}`) when the body carries no `data`. Consumers overlay
 * whatever keys are present on top of the existing TCM defaults, so an empty
 * theme renders identically to today.
 */
export const fetchGamePassTheme = async (): Promise<GamePassAdminTheme> => {
  const response = await getPrivateInstance().get<GamePassAdminThemeResponse>(
    API_ROUTES.GET_GAME_PASS_THEME,
  );

  return response.data?.data ?? {};
};

export const getGamePassThemeQueryOptions = (): {
  queryKey: QueryKey;
  queryFn: typeof fetchGamePassTheme;
  staleTime: number;
  retry: typeof retryOnTransientErrors;
} => ({
  queryKey: [...GAME_PASS_THEME_QUERY_KEY],
  queryFn: fetchGamePassTheme,
  staleTime: 1000 * 30,
  retry: retryOnTransientErrors,
});

export const useGetGamePassTheme = (): UseQueryResult<
  GamePassAdminTheme,
  Error
> =>
  useQuery<GamePassAdminTheme, Error>({
    ...getGamePassThemeQueryOptions(),
    refetchOnWindowFocus: false,
  });
